import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Button, Alert, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";

/**
 * ResultatSoloBoxe
 *
 * Page de récapitulatif pour un jugement "1 boxeur".
 * - Affiche le boxeur jugé, son score et le nom de l'arbitre.
 */

export default function ResultatSoloBoxe({ result: propResult, onHome }) {
  const location = useLocation();
  const navigate = useNavigate();
  const raw = propResult || location?.state?.match;
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [boxerInfo, setBoxerInfo] = useState({ id: null, fullName: null });
  const [arbitreInfo, setArbitreInfo] = useState({ id: null, fullName: null });
  const [classeName, setClasseName] = useState(null);

  const normalizeMatch = (m) => {
    if (!m) return null;
    return {
      params: m.params || {},
      soloBoxerId: m.soloBoxerId ?? m.boxerId ?? (m.participants ? (m.participants.boxerId || null) : null),
      soloBoxerName: m.soloBoxerName ?? m.boxerName ?? (m.participants ? (m.participants.nomBoxer || null) : null),
      soloBoxerPoints: m.soloBoxerPoints ?? m.boxerPoints ?? m.points ?? null,
      arbitreId: m.arbitreId ?? (m.participants ? m.participants.arbitreId : null),
      arbitreName: m.arbitreName ?? (m.participants ? (m.participants.nomArbitre || null) : null),
      winner: m.winner ?? null,
      winnerName: m.winnerName ?? null,
      createdAt: m.createdAt || null,
      rounds: m.rounds || []
    };
  };

  const computeSoloPoints = (matchData) => {
    if (typeof matchData?.soloBoxerPoints === "number") return matchData.soloBoxerPoints;
    if (typeof matchData?.boxerPoints === "number") return matchData.boxerPoints;
    if (typeof matchData?.points === "number") return matchData.points;
    return (matchData?.rounds || []).reduce((total, round) => {
      return total + (round?.scoreBoxer?.poings || 0) + (round?.scoreBoxer?.pieds || 0);
    }, 0);
  };

  const match = normalizeMatch(raw);

  useEffect(() => {
    let mounted = true;
    if (!match) return () => { mounted = false; };

    async function fetchNames() {
      try {
        const fetches = [];
        if (match.soloBoxerId) {
          fetches.push(api.get(`/eleves/${encodeURIComponent(match.soloBoxerId)}`).then(r => ({ type: 'boxer', data: r.data })).catch(() => ({ type: 'boxer', data: null })));
        } else {
          fetches.push(Promise.resolve({ type: 'boxer', data: null }));
        }
        if (match.arbitreId) {
          fetches.push(api.get(`/eleves/${encodeURIComponent(match.arbitreId)}`).then(r => ({ type: 'arbitre', data: r.data })).catch(() => ({ type: 'arbitre', data: null })));
        } else {
          fetches.push(Promise.resolve({ type: 'arbitre', data: null }));
        }

        const results = await Promise.all(fetches);
        if (!mounted) return;
        results.forEach(r => {
          if (r.type === 'boxer') {
            const e = r.data || {};
            const full = (e.prenom || e.nom) ? `${e.prenom ?? ''} ${e.nom ?? ''}`.trim() : null;
            setBoxerInfo({ id: match.soloBoxerId || null, fullName: full || match.soloBoxerName || "Boxeur" });
          } else if (r.type === 'arbitre') {
            const e = r.data || {};
            const full = (e.prenom || e.nom) ? `${e.prenom ?? ''} ${e.nom ?? ''}`.trim() : null;
            setArbitreInfo({ id: match.arbitreId || null, fullName: full || match.arbitreName || "—" });
          }
        });

        // classe name
        const cid = match.params?.classeId ?? match.params?.classe ?? null;
        if (cid) {
          try {
            const res = await api.get(`/classes/${encodeURIComponent(cid)}`);
            const c = res.data || {};
            const name = c.nom ?? c.name ?? c.label ?? c.title ?? String(cid);
            if (mounted) setClasseName(name);
          } catch (err) {
            if (mounted) setClasseName(String(cid));
          }
        } else {
          if (mounted) setClasseName(match.params?.classeName ?? "—");
        }
      } catch (err) {
        // ignore silent
      }
    }

    fetchNames();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  if (!match) {
    return <Box sx={{ p: 4 }}><Alert severity="error">Aucun jugement à afficher.</Alert></Box>;
  }

  const displayWinnerName = () => {
    if (match.winnerName && String(match.winnerName).trim() !== "") return String(match.winnerName);
    if (boxerInfo.fullName) return boxerInfo.fullName;
    return match.soloBoxerName || "Boxeur";
  };

  const handleReturn = () => {
    if (typeof onHome === "function") {
      onHome();
      return;
    }
    navigate("/apps/juge-boxe");
  };

  const handleSave = async () => {
    setMsg(null);
    setSaving(true);
    try {
      const payload = {
        mode: 'solo',
        classeId: match.params?.classeId ?? match.params?.classe ?? null,
        activity: match.params?.activity ?? match.params?.activityName ?? "Boxe Française",
        boxerId: match.soloBoxerId || null,
        boxerPoints: computeSoloPoints(match),
        arbitreId: match.arbitreId || null,
        params: match.params || {},
        rounds: match.rounds || [],
        createdAt: match.createdAt || new Date().toISOString()
      };

      await api.post("/matchs", payload);
      setMsg({ type: "success", text: "Jugement sauvegardé." });
      setTimeout(() => navigate("/apps/juge-boxe"), 900);
    } catch (err) {
      console.error("Save solo match failed:", err);
      const message = err?.response?.data?.error || err?.message || "Erreur lors de l'enregistrement.";
      setMsg({ type: "error", text: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa", display: "flex", flexDirection: "column", alignItems: "center", py: 3 }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, width: { xs: "95%", sm: 640 }, borderRadius: 4 }}>
        {/* header: activity + class */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 1, mb: 2.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{match.params?.activity ?? "Boxe Française"}</Typography>
            <Typography variant="subtitle2" sx={{ color: "#666" }}>{classeName || "—"}</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "#999" }}>{new Date(match.createdAt || Date.now()).toLocaleString()}</Typography>
        </Box>

        <Box sx={{ bgcolor: "#f5faff", borderRadius: 4, px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 }, textAlign: "center", mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: "#1565c0", fontWeight: 800, letterSpacing: 0.4 }}>BOXEUR JUGÉ</Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.75 }}>{displayWinnerName()}</Typography>
          <Typography sx={{ fontSize: { xs: 54, sm: 68 }, fontWeight: 900, color: "#1565c0", lineHeight: 1, mt: 1.5 }}>
            {computeSoloPoints(match)}
          </Typography>
          <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 1 }}>Points attribués</Typography>
        </Box>

        <Box sx={{ textAlign: "center", mb: 2, bgcolor: "#faf6ff", borderRadius: 4, px: 2, py: 2 }}>
          <Typography variant="subtitle2" sx={{ color: "#666" }}>Juge Arbitre</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>{arbitreInfo.fullName || "—"}</Typography>
        </Box>

        {msg && <Alert severity={msg.type} sx={{ mt: 2 }}>{msg.text}</Alert>}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, mt: 3 }}>
          <Button variant="contained" color="success" onClick={handleSave} disabled={saving} sx={{ minHeight: 50, fontWeight: 800 }}>
            {saving ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Sauvegarde...</> : "Sauvegarder le jugement"}
          </Button>

          <Button variant="outlined" onClick={handleReturn} disabled={saving} sx={{ minHeight: 50, fontWeight: 800 }}>Retour</Button>
        </Box>
      </Paper>
    </Box>
  );
}